import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  FormControlLabel,
  Switch,
  Grid,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { produtoService, ProdutoCommand, Produto } from '../../services/produto';
import SelectMarca from '../marca/SelectMarca';
import SelectLinha from '../linha/SelectLinha';

interface ProdutoFormProps {
  isEdit?: boolean;
}

const ProdutoForm: React.FC<ProdutoFormProps> = ({ isEdit = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<ProdutoCommand>({
    nomeProduto: '',
    descricao: '',
    codMarca: 0,
    codLinha: 0,
    precoVenda: 0,
    referencia: '',
    comissaoPersonalizada: 0,
    semComissao: 0,
  });

  const [precoVendaFormatado, setPrecoVendaFormatado] = useState('');

  const [errors, setErrors] = useState({
    nomeProduto: '',
    descricao: '',
    codMarca: '',
    codLinha: '',
    precoVenda: '',
    referencia: '',
    comissaoPersonalizada: '',
  });

  const [referenciaValida, setReferenciaValida] = useState<'valid' | 'invalid' | null>(null);
  const [validandoReferencia, setValidandoReferencia] = useState(false);

  // Buscar produto para edição
  const { data: produto, isLoading, error } = useQuery({
    queryKey: ['produto', id],
    queryFn: () => produtoService.getById(Number(id)),
    enabled: !!id && isEdit,
  });

  // Mutação para salvar
  const saveMutation = useMutation({
    mutationFn: produtoService.save,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produtos'] });
      navigate('/produtos');
    },
    onError: (error: any) => {
      console.error('Erro ao salvar produto:', error);
    },
  });

  useEffect(() => {
    if (produto && isEdit) {
      setFormData({
        id: produto.id,
        nomeProduto: produto.nomeProduto,
        descricao: produto.descricao,
        codMarca: produto.codMarca,
        codLinha: produto.codLinha,
        precoVenda: produto.precoVenda,
        referencia: produto.referencia,
        comissaoPersonalizada: produto.comissaoPersonalizada,
        semComissao: produto.semComissao,
      });
      // Formatar preço para exibição
      setPrecoVendaFormatado(produto.precoVenda.toFixed(2).replace('.', ','));
    }
  }, [produto, isEdit]);

  const handleInputChange = (field: keyof ProdutoCommand) => (event: any) => {
    const value = event.target.type === 'number' ? Number(event.target.value) : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpar erro do campo
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePrecoVendaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    
    // Permitir apenas números, vírgula e ponto
    const regex = /^[0-9]*[.,]?[0-9]*$/;
    if (value === '' || regex.test(value)) {
      setPrecoVendaFormatado(value);
      
      // Converter para número (substituir vírgula por ponto)
      const valorNumerico = value.replace(',', '.');
      const precoVenda = parseFloat(valorNumerico) || 0;
      
      // Atualizar o estado imediatamente
      setFormData(prev => ({ ...prev, precoVenda }));
      
      // Limpar erro do campo
      if (errors.precoVenda) {
        setErrors(prev => ({ ...prev, precoVenda: '' }));
      }
    }
  };

  const handleMarcaChange = (value: number) => {
    setFormData(prev => ({ ...prev, codMarca: value }));
    if (errors.codMarca) {
      setErrors(prev => ({ ...prev, codMarca: '' }));
    }
  };

  const handleLinhaChange = (value: number) => {
    setFormData(prev => ({ ...prev, codLinha: value }));
    if (errors.codLinha) {
      setErrors(prev => ({ ...prev, codLinha: '' }));
    }
  };

  const handleSemComissaoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const semComissao = event.target.checked ? 1 : 0;
    setFormData(prev => ({ ...prev, semComissao }));
  };

  const validarReferencia = async () => {
    if (!formData.referencia || (isEdit && id)) return;

    setValidandoReferencia(true);
    try {
      const disponivel = await produtoService.verificarReferenciaDisponivel(formData.referencia);
      setReferenciaValida(disponivel ? 'valid' : 'invalid');
    } catch (error) {
      setReferenciaValida('invalid');
    } finally {
      setValidandoReferencia(false);
    }
  };

  const handleReferenciaBlur = () => {
    validarReferencia();
  };

  const validateForm = () => {
    const newErrors = {
      nomeProduto: '',
      descricao: '',
      codMarca: '',
      codLinha: '',
      precoVenda: '',
      referencia: '',
      comissaoPersonalizada: '',
    };
    let isValid = true;

    // Validação Nome
    if (!formData.nomeProduto.trim()) {
      newErrors.nomeProduto = 'Nome é obrigatório';
      isValid = false;
    } else if (formData.nomeProduto.length < 3) {
      newErrors.nomeProduto = 'Nome deve ter pelo menos 3 caracteres';
      isValid = false;
    } else if (formData.nomeProduto.length > 80) {
      newErrors.nomeProduto = 'Nome não pode ter mais de 80 caracteres';
      isValid = false;
    }

    // Validação Descrição
    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Descrição é obrigatória';
      isValid = false;
    } else if (formData.descricao.length > 80) {
      newErrors.descricao = 'Descrição não pode ter mais de 80 caracteres';
      isValid = false;
    }

    // Validação Marca
    if (!formData.codMarca) {
      newErrors.codMarca = 'Marca é obrigatória';
      isValid = false;
    }

    // Validação Linha
    if (!formData.codLinha) {
      newErrors.codLinha = 'Linha é obrigatória';
      isValid = false;
    }

    // Validação Preço
    if (!formData.precoVenda || formData.precoVenda <= 0) {
      newErrors.precoVenda = 'Preço de venda é obrigatório e deve ser maior que 0';
      isValid = false;
    }

    // Validação Referência
    if (!formData.referencia.trim()) {
      newErrors.referencia = 'Referência é obrigatória';
      isValid = false;
    } else if (formData.referencia.length > 5) {
      newErrors.referencia = 'Referência não pode ter mais de 5 caracteres';
      isValid = false;
    } else if (referenciaValida === 'invalid' && !isEdit) {
      newErrors.referencia = 'Referência já está em uso';
      isValid = false;
    }

    // Validação Comissão
    if (formData.comissaoPersonalizada < 0 || formData.comissaoPersonalizada > 100) {
      newErrors.comissaoPersonalizada = 'Comissão deve estar entre 0 e 100';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    
    if (validateForm()) {
      // Converter o preço formatado para número e string com ponto decimal
      const precoVendaNumerico = parseFloat(precoVendaFormatado.replace(',', '.')) || 0;
      const precoVendaString = precoVendaFormatado.replace(',', '.'); // Backend espera ponto
      
      const dataToSave = {
        ...formData,
        precoVenda: precoVendaNumerico,
        precoVendaString: precoVendaString,
        id: isEdit ? Number(id) : undefined,
      };
      
      saveMutation.mutate(dataToSave);
    }
  };

  const handleBack = () => {
    navigate('/produtos');
  };

  const handleClear = () => {
    setFormData({
      nomeProduto: '',
      descricao: '',
      codMarca: 0,
      codLinha: 0,
      precoVenda: 0,
      referencia: '',
      comissaoPersonalizada: 0,
      semComissao: 0,
    });
    setPrecoVendaFormatado('');
    setErrors({
      nomeProduto: '',
      descricao: '',
      codMarca: '',
      codLinha: '',
      precoVenda: '',
      referencia: '',
      comissaoPersonalizada: '',
    });
    setReferenciaValida(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && isEdit) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Erro ao carregar os dados do produto.
        </Alert>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <TextField
              fullWidth
              label="Nome do Produto"
              value={formData.nomeProduto}
              onChange={handleInputChange('nomeProduto')}
              error={!!errors.nomeProduto}
              helperText={errors.nomeProduto}
              required
              slotProps={{
                htmlInput: { maxLength: 80 }
              }}
            />
            
            <TextField
              fullWidth
              label="Descrição"
              value={formData.descricao}
              onChange={handleInputChange('descricao')}
              error={!!errors.descricao}
              helperText={errors.descricao}
              required
              slotProps={{
                htmlInput: { maxLength: 80 }
              }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <SelectMarca
              value={formData.codMarca}
              onChange={handleMarcaChange}
              error={!!errors.codMarca}
              helperText={errors.codMarca}
              required
            />
            
            <SelectLinha
              value={formData.codLinha}
              onChange={handleLinhaChange}
              error={!!errors.codLinha}
              helperText={errors.codLinha}
              required
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
            <TextField
              fullWidth
              label="Comissão Personalizada (%)"
              type="number"
              value={formData.comissaoPersonalizada}
              onChange={handleInputChange('comissaoPersonalizada')}
              error={!!errors.comissaoPersonalizada}
              helperText={errors.comissaoPersonalizada}
              slotProps={{
                htmlInput: { min: 0, max: 100, step: 0.1 }
              }}
            />
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.semComissao === 1}
                    onChange={handleSemComissaoChange}
                  />
                }
                label="Produto Sem Comissão"
              />
            </Box>
            
            <TextField
              fullWidth
              label="Referência"
              value={formData.referencia}
              onChange={handleInputChange('referencia')}
              onBlur={handleReferenciaBlur}
              error={!!errors.referencia || referenciaValida === 'invalid'}
              helperText={errors.referencia || (referenciaValida === 'invalid' ? 'Referência já está em uso' : '')}
              required
              slotProps={{
                htmlInput: { maxLength: 5 },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {validandoReferencia ? (
                        <CircularProgress size={20} />
                      ) : referenciaValida === 'valid' ? (
                        <CheckCircleIcon color="success" />
                      ) : referenciaValida === 'invalid' ? (
                        <ErrorIcon color="error" />
                      ) : null}
                    </InputAdornment>
                  ),
                }
              }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr' }, gap: 3 }}>
            <TextField
              fullWidth
              label="Preço de Venda (R$)"
              value={precoVendaFormatado}
              onChange={handlePrecoVendaChange}
              error={!!errors.precoVenda}
              helperText={errors.precoVenda || "Digite o preço com vírgula decimal (ex: 15,50)"}
              required
              placeholder="0,00"
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saveMutation.isPending}
              sx={{ minWidth: 120 }}
            >
              {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClear}
            >
              Limpar
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
              Voltar
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default ProdutoForm;
